// AtRiskQueue — the dashboard's "At risk" tile and queue, from C's retention
// queue: customer · issue · days open · owner · SLA state · one-tap Call. Renders
// nothing while the API has no retention route (the leg is C's), and an honest
// empty line when the queue is clear. Vocabulary comes from the industry copy.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import { fetchRetentionQueue, issueTone, daysOpen } from "../../api/retentionApi";
import { sourceSentence } from "../../components/Customers/AtRisk/IssueCard";
import { Table, THead, TRow, TCell } from "../../components/ui/table";
import Badge from "../../components/ui/badge";
import Button from "../../components/ui/button";
import { telHref } from "../../components/Profile/customerFormat";
import "../../components/Customers/AtRisk/atRisk.css";

const SLA_WORD = { ok: "On time", due_soon: "Due soon", breached: "Overdue" };

export function useRetentionQueue(limit = 8) {
  const [state, setState] = useState({ loading: true, queue: null, error: false });
  useEffect(() => {
    let alive = true;
    fetchRetentionQueue({ limit })
      .then((queue) => { if (alive) setState({ loading: false, queue, error: false }); })
      .catch(() => { if (alive) setState({ loading: false, queue: null, error: true }); });
    return () => { alive = false; };
  }, [limit]);
  return state;
}

/** The tile: a count with the worst SLA state as its tone. null when the route is absent. */
export function AtRiskTile({ queue, copy, onOpen }) {
  if (!queue) return null;
  const items = queue.items || [];
  const breached = items.filter((i) => i.sla?.state === "breached").length;
  const noun = (copy?.customerPlural || "customers").toLowerCase();
  return (
    <button type="button" className={`dash-card dash-kpi dash-kpi--risk${queue.count ? "" : " is-clear"}`} onClick={onOpen} aria-label={`At risk: ${queue.count} ${noun}`}>
      <div className="k-label">At risk</div>
      <div className="k-value">{queue.count}</div>
      <div className={`k-delta${breached ? " down" : ""}`}>{queue.count ? `${noun} with an open issue${breached ? ` · ${breached} overdue` : ""}` : `no ${noun} with an open issue`}</div>
    </button>
  );
}

export default function AtRiskQueue({ queue, loading, copy, onOpenCustomer }) {
  const navigate = useNavigate();
  const noun = (copy?.customer || "Customer");
  const items = useMemo(() => (queue?.items || []).filter((i) => i.status !== "resolved"), [queue]);
  if (!queue) return null; // the route does not exist on this API yet

  const call = (issue) => {
    const phone = issue.action?.phone || issue.customer?.phone;
    if (phone) window.location.href = telHref(phone);
    else if (issue.customer?.id) onOpenCustomer?.(issue.customer);
  };

  return (
    <section className="atq" aria-label="At-risk queue">
      <div className="atq__head">
        <h3 className="atq__title">At risk</h3>
        <span className="atq__sub">{loading ? "Loading…" : queue.count ? `${queue.count} open · oldest first` : "Nothing open"}</span>
      </div>
      {items.length ? (
        <div className="atq__table">
          <Table>
            <THead columns={[noun, "Issue", "Days open", "Owner", "SLA", ""]} />
            <tbody>
              {items.map((i) => (
                <TRow key={i.id} onClick={() => (i.customer?.id ? onOpenCustomer?.(i.customer) : navigate("/customers/list"))} className="is-clickable">
                  <TCell><b>{i.customer?.name || "—"}</b></TCell>
                  <TCell>
                    <span className="atq__issue">
                      <b>{i.title || i.issue || "Needs a look"}</b>
                      <span>{i.quote ? `“${i.quote}”` : sourceSentence(i)}</span>
                    </span>
                  </TCell>
                  <TCell className="is-num">{daysOpen(i) ?? "—"}</TCell>
                  <TCell>{i.owner?.name || <span className="atq__sub">Nobody yet</span>}</TCell>
                  <TCell><Badge tone={issueTone(i)}>{SLA_WORD[i.sla?.state] || "—"}</Badge></TCell>
                  <TCell>
                    <Button size="sm" variant="secondary" icon={<Phone size={14} />} onClick={(e) => { e.stopPropagation(); call(i); }}>Call</Button>
                  </TCell>
                </TRow>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="atq__empty">{loading ? "Loading the queue…" : `No ${noun.toLowerCase()} has an open issue right now.`}</div>
      )}
    </section>
  );
}
